import cx from 'classnames';
import React, { useContext } from 'react';
import {
  type ButtonProps,
  PrimaryButton,
  SecondaryButton
} from '@/common/Button';
import DefaultFinanceCard from '@/common/FinanceCard/DefaultFinanceCard';
import TrackWrapper from '@/common/TrackWrapper';
import { fireAction } from '@/home/HeroCardsReturnUser/Cards/actionNames';
import LoadingCard, {
  CardType
} from '@/home/HeroCardsReturnUser/Cards/LoadingCard';
import useHasPurchaseInProgress from '@/home/hooks/useHasPurchaseInProgress';
import formatEvent from '@/home/utils/formatEvent';
import { FinanceCard as CommonCard } from '~/components/common/FinanceCard';
import { FinanceCardType } from '~/components/common/FinanceCard/constants';
import {
  calculateDaysUntilExpired,
  getFinanceCardType
} from '~/components/common/FinanceCard/utils';
import Sections from '~/constants/home/sections';
import { useIsNative } from '~/context/common/DeviceContext';
import {
  useGetExpirationDate,
  useHasExpiredSoftpull,
  useHasValidSoftPull,
  useIsFinanceLoaded
} from '~/context/home/FinanceContext';
import ModalContext from '~/context/home/ModalContext';
import { useGetUserPurchaseIsLoaded } from '~/context/home/UserPurchaseContext';
import { nativeSrpPayload } from '~/native/common/utils';
import { useNativeOpenSoftPullOnClick } from '~/native/home/hooks';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

const description = 'Finance card';

function FinanceCard() {
  const handleNativeOpenSoftPullOnClick = useNativeOpenSoftPullOnClick();
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const isNative = useIsNative();
  const expiredDate = useGetExpirationDate();
  const hasValidSoftPull = useHasValidSoftPull();
  const hasExpiredSoftPull = useHasExpiredSoftpull();
  const hasPurchaseInProgress = useHasPurchaseInProgress();

  const status = getFinanceCardType(hasValidSoftPull, hasExpiredSoftPull);
  const daysToExpired = calculateDaysUntilExpired(expiredDate);
  const isDefault = status === FinanceCardType.Default;
  const openSoftPullModal = hasExpiredSoftPull || isDefault;

  const cta = isDefault
    ? 'get pre-qualified'
    : hasExpiredSoftPull
    ? 'refresh your terms'
    : 'shop now';

  const ctaCommonProps = {
    'data-analytics-id': 'Home feed card',
    'data-analytics-attributes': JSON.stringify({
      cta,
      daysToExpired,
      description,
      status
    }),
    'data-native-included': !openSoftPullModal,
    'data-native-payload': !openSoftPullModal ? nativeSrpPayload : undefined,
    'data-qa': cta
  };

  const ctaAnchorProps: React.HTMLProps<HTMLAnchorElement> = {
    ...ctaCommonProps,
    href: '/cars',
    onClick: _ => {
      fireAction(CardType.Finance);
    }
  };

  const ctaButtonProps: ButtonProps = {
    ...ctaCommonProps,
    onClick: ev => {
      fireAction(CardType.Finance);
      handleNativeOpenSoftPullOnClick(ev);
      if (!isNative) setSoftPullModalOpen(true);
    }
  };

  const CtaButton =
    status === FinanceCardType.Expired ? PrimaryButton : SecondaryButton;

  return isDefault ? (
    <DefaultFinanceCard
      cta={
        <PrimaryButton
          className="h-40 min-h-40 w-full"
          mode="dark"
          {...ctaButtonProps}
        >
          Get pre-qualified
        </PrimaryButton>
      }
    />
  ) : (
    <TrackWrapper
      event={formatEvent('Viewed - Home feed card', {
        description,
        status,
        cta
      })}
      trackOnce
    >
      <div className="rounded-md border border-solid border-grey-2 p-24 text-blue-6 -:bg-white m:h-[216px] m:max-h-[216px] l:h-[211px] l:max-h-[211px]">
        <CommonCard
          cta={
            hasExpiredSoftPull ? (
              <CtaButton
                className={cx('h-40 min-h-40 w-full uppercase', {
                  hidden: hasPurchaseInProgress && hasValidSoftPull
                })}
                {...ctaButtonProps}
              >
                {cta}
              </CtaButton>
            ) : (
              <a
                className={cx({
                  hidden: hasPurchaseInProgress && hasValidSoftPull
                })}
                {...ctaAnchorProps}
              >
                <CtaButton className="h-40 min-h-40 w-full uppercase">
                  {cta}
                </CtaButton>
              </a>
            )
          }
          daysToExpired={daysToExpired}
          status={status}
          title="Your Financing"
        />
      </div>
    </TrackWrapper>
  );
}

function FinanceCardWrapper() {
  const isUserPurchaseLoaded = useGetUserPurchaseIsLoaded();
  const isFinanceLoaded = useIsFinanceLoaded();

  return isFinanceLoaded && isUserPurchaseLoaded ? (
    <FinanceCard />
  ) : (
    <LoadingCard card={CardType.Finance} />
  );
}

export default withErrorBoundary(
  FinanceCardWrapper,
  undefined,
  undefined,
  Sections.FinanceCard
);
